import { PartialTemplateValues, VehicleClassBucket } from "../types";
import {
  DEFAULT_BATTERY_REPLACEMENT_SCHEDULE,
  DEFAULT_BRAKE_SERVICE_SCHEDULE,
  DEFAULT_MAJOR_SERVICE_SCHEDULE,
  DEFAULT_MISC_MAINTENANCE_SCHEDULE,
  DEFAULT_REPAIR_BUFFER_SCHEDULE,
} from "./constants";

const yearlySchedule = (amount: number) => ({
  t: "t" as const,
  v: { n: amount, u: "year" as const },
});

const mileageSchedule = (amount: number) => ({
  t: "d" as const,
  v: { n: amount, u: "mile" as const },
});

// These are intentionally generic class-level defaults. They are meant to give
// users a better first-pass estimate than the one-size-fits-all global defaults,
// but they are not a substitute for vehicle-specific maintenance history or real
// insurance quotes.
//
// Current calibration notes:
// - Insurance anchors lean on AAA 2025 category data where the segment lines up
//   (small/medium sedan, subcompact SUV, compact SUV, medium SUV, pickup truck).
// - Vans/minivans and sporty two-seaters use broader vehicle-type insurance
//   averages as a fallback because AAA does not publish those exact buckets.
// - Maintenance-related defaults are an inference layered on top of AAA's
//   maintenance cents-per-mile guidance at 15k mi/yr. The goal is for the
//   annualized oil, tires, maintenance, brakes, battery, major service, and
//   repair reserve to land in the same ballpark as the published segment cost.
export const VEHICLE_CLASS_DEFAULTS: Record<
  VehicleClassBucket,
  PartialTemplateValues
> = {
  unknown: {},
  two_seater: {
    annualInsurance: 2850,
    oilChangeCost: 120,
    oilChangeInterval: 7500,
    tireCost: 1200,
    tireInterval: 30000,
    miscMaintenanceCost: 650,
    miscMaintenanceSchedule: yearlySchedule(1),
    brakeServiceCost: 800,
    brakeServiceSchedule: mileageSchedule(35000),
    batteryReplacementCost: 240,
    batteryReplacementSchedule: DEFAULT_BATTERY_REPLACEMENT_SCHEDULE,
    majorServiceCost: 350,
    majorServiceSchedule: DEFAULT_MAJOR_SERVICE_SCHEDULE,
    repairBufferCost: 175,
    repairBufferSchedule: DEFAULT_REPAIR_BUFFER_SCHEDULE,
  },
  subcompact: {
    annualInsurance: 1550,
    oilChangeCost: 60,
    oilChangeInterval: 7500,
    tireCost: 800,
    tireInterval: 50000,
    miscMaintenanceCost: 650,
    miscMaintenanceSchedule: DEFAULT_MISC_MAINTENANCE_SCHEDULE,
    brakeServiceCost: 550,
    brakeServiceSchedule: DEFAULT_BRAKE_SERVICE_SCHEDULE,
    batteryReplacementCost: 180,
    batteryReplacementSchedule: DEFAULT_BATTERY_REPLACEMENT_SCHEDULE,
    majorServiceCost: 275,
    majorServiceSchedule: DEFAULT_MAJOR_SERVICE_SCHEDULE,
    repairBufferCost: 200,
    repairBufferSchedule: DEFAULT_REPAIR_BUFFER_SCHEDULE,
  },
  compact: {
    annualInsurance: 1511,
    oilChangeCost: 65,
    oilChangeInterval: 7500,
    tireCost: 850,
    tireInterval: 50000,
    miscMaintenanceCost: 675,
    miscMaintenanceSchedule: DEFAULT_MISC_MAINTENANCE_SCHEDULE,
    brakeServiceCost: 575,
    brakeServiceSchedule: DEFAULT_BRAKE_SERVICE_SCHEDULE,
    batteryReplacementCost: 190,
    batteryReplacementSchedule: DEFAULT_BATTERY_REPLACEMENT_SCHEDULE,
    majorServiceCost: 300,
    majorServiceSchedule: DEFAULT_MAJOR_SERVICE_SCHEDULE,
    repairBufferCost: 225,
    repairBufferSchedule: DEFAULT_REPAIR_BUFFER_SCHEDULE,
  },
  midsize: {
    annualInsurance: 1572,
    oilChangeCost: 75,
    oilChangeInterval: 7500,
    tireCost: 950,
    tireInterval: 50000,
    miscMaintenanceCost: 775,
    miscMaintenanceSchedule: DEFAULT_MISC_MAINTENANCE_SCHEDULE,
    brakeServiceCost: 650,
    brakeServiceSchedule: DEFAULT_BRAKE_SERVICE_SCHEDULE,
    batteryReplacementCost: 200,
    batteryReplacementSchedule: DEFAULT_BATTERY_REPLACEMENT_SCHEDULE,
    majorServiceCost: 325,
    majorServiceSchedule: DEFAULT_MAJOR_SERVICE_SCHEDULE,
    repairBufferCost: 250,
    repairBufferSchedule: DEFAULT_REPAIR_BUFFER_SCHEDULE,
  },
  large: {
    annualInsurance: 1700,
    oilChangeCost: 85,
    oilChangeInterval: 7500,
    tireCost: 1050,
    tireInterval: 50000,
    miscMaintenanceCost: 800,
    miscMaintenanceSchedule: DEFAULT_MISC_MAINTENANCE_SCHEDULE,
    brakeServiceCost: 700,
    brakeServiceSchedule: DEFAULT_BRAKE_SERVICE_SCHEDULE,
    batteryReplacementCost: 220,
    batteryReplacementSchedule: DEFAULT_BATTERY_REPLACEMENT_SCHEDULE,
    majorServiceCost: 350,
    majorServiceSchedule: DEFAULT_MAJOR_SERVICE_SCHEDULE,
    repairBufferCost: 250,
    repairBufferSchedule: DEFAULT_REPAIR_BUFFER_SCHEDULE,
  },
  minivan: {
    annualInsurance: 2084,
    oilChangeCost: 80,
    oilChangeInterval: 7500,
    tireCost: 1000,
    tireInterval: 50000,
    miscMaintenanceCost: 700,
    miscMaintenanceSchedule: DEFAULT_MISC_MAINTENANCE_SCHEDULE,
    brakeServiceCost: 650,
    brakeServiceSchedule: DEFAULT_BRAKE_SERVICE_SCHEDULE,
    batteryReplacementCost: 220,
    batteryReplacementSchedule: DEFAULT_BATTERY_REPLACEMENT_SCHEDULE,
    majorServiceCost: 325,
    majorServiceSchedule: DEFAULT_MAJOR_SERVICE_SCHEDULE,
    repairBufferCost: 225,
    repairBufferSchedule: DEFAULT_REPAIR_BUFFER_SCHEDULE,
  },
  heavy_duty_truck: {
    annualInsurance: 2400,
    oilChangeCost: 140,
    oilChangeInterval: 6000,
    tireCost: 1500,
    tireInterval: 40000,
    miscMaintenanceCost: 900,
    miscMaintenanceSchedule: DEFAULT_MISC_MAINTENANCE_SCHEDULE,
    brakeServiceCost: 900,
    brakeServiceSchedule: mileageSchedule(35000),
    batteryReplacementCost: 260,
    batteryReplacementSchedule: DEFAULT_BATTERY_REPLACEMENT_SCHEDULE,
    majorServiceCost: 500,
    majorServiceSchedule: DEFAULT_MAJOR_SERVICE_SCHEDULE,
    repairBufferCost: 350,
    repairBufferSchedule: DEFAULT_REPAIR_BUFFER_SCHEDULE,
  },
  small_pickup_truck: {
    annualInsurance: 1527,
    oilChangeCost: 85,
    oilChangeInterval: 7000,
    tireCost: 1000,
    tireInterval: 50000,
    miscMaintenanceCost: 650,
    miscMaintenanceSchedule: DEFAULT_MISC_MAINTENANCE_SCHEDULE,
    brakeServiceCost: 600,
    brakeServiceSchedule: mileageSchedule(45000),
    batteryReplacementCost: 220,
    batteryReplacementSchedule: DEFAULT_BATTERY_REPLACEMENT_SCHEDULE,
    majorServiceCost: 325,
    majorServiceSchedule: DEFAULT_MAJOR_SERVICE_SCHEDULE,
    repairBufferCost: 250,
    repairBufferSchedule: DEFAULT_REPAIR_BUFFER_SCHEDULE,
  },
  small_suv: {
    annualInsurance: 1695,
    oilChangeCost: 80,
    oilChangeInterval: 7500,
    tireCost: 900,
    tireInterval: 50000,
    miscMaintenanceCost: 675,
    miscMaintenanceSchedule: DEFAULT_MISC_MAINTENANCE_SCHEDULE,
    brakeServiceCost: 600,
    brakeServiceSchedule: mileageSchedule(45000),
    batteryReplacementCost: 220,
    batteryReplacementSchedule: DEFAULT_BATTERY_REPLACEMENT_SCHEDULE,
    majorServiceCost: 300,
    majorServiceSchedule: DEFAULT_MAJOR_SERVICE_SCHEDULE,
    repairBufferCost: 175,
    repairBufferSchedule: DEFAULT_REPAIR_BUFFER_SCHEDULE,
  },
  special_purpose: {
    annualInsurance: 2100,
    oilChangeCost: 95,
    oilChangeInterval: 7000,
    tireCost: 1050,
    tireInterval: 45000,
    miscMaintenanceCost: 700,
    miscMaintenanceSchedule: DEFAULT_MISC_MAINTENANCE_SCHEDULE,
    brakeServiceCost: 700,
    brakeServiceSchedule: mileageSchedule(40000),
    batteryReplacementCost: 240,
    batteryReplacementSchedule: DEFAULT_BATTERY_REPLACEMENT_SCHEDULE,
    majorServiceCost: 350,
    majorServiceSchedule: DEFAULT_MAJOR_SERVICE_SCHEDULE,
    repairBufferCost: 125,
    repairBufferSchedule: DEFAULT_REPAIR_BUFFER_SCHEDULE,
  },
  suv: {
    annualInsurance: 1833,
    oilChangeCost: 90,
    oilChangeInterval: 7000,
    tireCost: 1000,
    tireInterval: 45000,
    miscMaintenanceCost: 725,
    miscMaintenanceSchedule: DEFAULT_MISC_MAINTENANCE_SCHEDULE,
    brakeServiceCost: 700,
    brakeServiceSchedule: mileageSchedule(40000),
    batteryReplacementCost: 230,
    batteryReplacementSchedule: DEFAULT_BATTERY_REPLACEMENT_SCHEDULE,
    majorServiceCost: 350,
    majorServiceSchedule: DEFAULT_MAJOR_SERVICE_SCHEDULE,
    repairBufferCost: 125,
    repairBufferSchedule: DEFAULT_REPAIR_BUFFER_SCHEDULE,
  },
  standard_pickup_truck: {
    annualInsurance: 1699,
    oilChangeCost: 100,
    oilChangeInterval: 6500,
    tireCost: 1100,
    tireInterval: 45000,
    miscMaintenanceCost: 575,
    miscMaintenanceSchedule: DEFAULT_MISC_MAINTENANCE_SCHEDULE,
    brakeServiceCost: 650,
    brakeServiceSchedule: mileageSchedule(40000),
    batteryReplacementCost: 250,
    batteryReplacementSchedule: DEFAULT_BATTERY_REPLACEMENT_SCHEDULE,
    majorServiceCost: 325,
    majorServiceSchedule: DEFAULT_MAJOR_SERVICE_SCHEDULE,
    repairBufferCost: 150,
    repairBufferSchedule: DEFAULT_REPAIR_BUFFER_SCHEDULE,
  },
  standard_suv: {
    annualInsurance: 1950,
    oilChangeCost: 95,
    oilChangeInterval: 6500,
    tireCost: 1100,
    tireInterval: 45000,
    miscMaintenanceCost: 675,
    miscMaintenanceSchedule: DEFAULT_MISC_MAINTENANCE_SCHEDULE,
    brakeServiceCost: 725,
    brakeServiceSchedule: mileageSchedule(40000),
    batteryReplacementCost: 240,
    batteryReplacementSchedule: DEFAULT_BATTERY_REPLACEMENT_SCHEDULE,
    majorServiceCost: 375,
    majorServiceSchedule: DEFAULT_MAJOR_SERVICE_SCHEDULE,
    repairBufferCost: 150,
    repairBufferSchedule: DEFAULT_REPAIR_BUFFER_SCHEDULE,
  },
  van_passenger: {
    annualInsurance: 2073,
    oilChangeCost: 90,
    oilChangeInterval: 7000,
    tireCost: 1000,
    tireInterval: 45000,
    miscMaintenanceCost: 675,
    miscMaintenanceSchedule: DEFAULT_MISC_MAINTENANCE_SCHEDULE,
    brakeServiceCost: 650,
    brakeServiceSchedule: mileageSchedule(40000),
    batteryReplacementCost: 230,
    batteryReplacementSchedule: DEFAULT_BATTERY_REPLACEMENT_SCHEDULE,
    majorServiceCost: 325,
    majorServiceSchedule: DEFAULT_MAJOR_SERVICE_SCHEDULE,
    repairBufferCost: 150,
    repairBufferSchedule: DEFAULT_REPAIR_BUFFER_SCHEDULE,
  },
  van_cargo: {
    annualInsurance: 2150,
    oilChangeCost: 95,
    oilChangeInterval: 6500,
    tireCost: 1100,
    tireInterval: 45000,
    miscMaintenanceCost: 700,
    miscMaintenanceSchedule: DEFAULT_MISC_MAINTENANCE_SCHEDULE,
    brakeServiceCost: 700,
    brakeServiceSchedule: mileageSchedule(40000),
    batteryReplacementCost: 240,
    batteryReplacementSchedule: DEFAULT_BATTERY_REPLACEMENT_SCHEDULE,
    majorServiceCost: 350,
    majorServiceSchedule: DEFAULT_MAJOR_SERVICE_SCHEDULE,
    repairBufferCost: 175,
    repairBufferSchedule: DEFAULT_REPAIR_BUFFER_SCHEDULE,
  },
};

export const getVehicleClassDefaultValues = (
  bucket: VehicleClassBucket,
): PartialTemplateValues => VEHICLE_CLASS_DEFAULTS[bucket] ?? {};
